/** The words of the tile label: what the tile is and what it is good for (what stands on it: its name). */
import {
  GROWS,
  KIND,
  MACHINE_TYPES,
  MACHINES,
  RULES,
  type Kind,
} from '@bramvasterd/terrella-rules';

import type { TileFacts } from '@game/planet/tile';
import { duration, kindName, machineName, resourceName, t } from '@i18n/index';

/** "A crystal resonator draws from it.", "2 crystal resonators share it.": ground being drawn from. */
function drawnHint(kind: Kind, n: number): string {
  const type = MACHINE_TYPES.find((x) => {
    const spec = MACHINES[x];
    return spec.role === 'extractor' && spec.ground === kind;
  });
  if (!type) return '';
  return t('hint.drawn', {
    n,
    name: machineName(type).toLowerCase(),
    names: machineName(type, true),
  });
}

/** What a plain tile is good for. */
const GROUND_HINT: Partial<Record<Kind, Parameters<typeof t>[0]>> = {
  [KIND.STONE]: 'hint.stone',
  [KIND.SOIL]: 'hint.soil',
  [KIND.BASALT]: 'hint.basalt',
  [KIND.GOLD]: 'hint.gold',
  [KIND.CORE]: 'hint.core',
  [KIND.TREE]: 'hint.tree',
  [KIND.WATER]: 'hint.water',
  [KIND.ORE]: 'hint.ore',
  [KIND.HUB]: 'hint.hub',
};

/** Names for a group of one kind: a forest, deep water. */
const GROUP_TITLE: Partial<Record<Kind, Parameters<typeof t>[0]>> = {
  [KIND.TREE]: 'group.tree',
  [KIND.WATER]: 'group.water',
};

export function tileText(facts: TileFacts): { title: string; hint: string } {
  const { kind } = facts;
  // in a pit: how deep, before the layer that is its floor; on laid blocks: how high
  const level =
    facts.elev < 0
      ? t('tile.depth', { n: -facts.elev })
      : facts.elev > 0
        ? t('tile.height', { n: facts.elev })
        : '';
  // what it holds and what it draws from is behind its Info button; a charge says when it goes off
  if (facts.machine) {
    const { machine } = facts;
    const hint =
      machine.role === 'charge'
        ? t('hint.fuse', { time: duration(machine.left) })
        : machine.role === 'infuser'
          ? machine.lay
            ? t('hint.lay', {
                block: resourceName(machine.lay.block),
                time: duration(machine.lay.left),
              })
            : t(machine.idle === 'nowhere' ? 'hint.nowhere' : 'hint.load')
          : '';
    return { title: level + machineName(machine.type), hint };
  }
  const groupKey = facts.group ? GROUP_TITLE[kind] : undefined;
  // rock a charge broke: "Broken stone", to be gathered
  const named = kindName(kind);
  const title = groupKey
    ? t(groupKey)
    : facts.broken
      ? t('kind.broken', { kind: named.toLowerCase() })
      : named;
  let hint = '';
  if (facts.broken) {
    hint = t('hint.broken');
  } else if (GROWS.has(kind)) {
    // a sapling planted this very moment reads as the whole growing time
    const left = facts.growsIn !== null && facts.growsIn > 0 ? facts.growsIn : RULES.growMs;
    hint = t('hint.sapling', { time: duration(left) });
  } else if (facts.drawnBy > 0) {
    hint = drawnHint(kind, facts.drawnBy);
  } else {
    const key = GROUND_HINT[kind];
    if (key) hint = t(key);
  }
  return { title: level + title, hint };
}
